import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/api';
import { Mail, Save, X, RotateCcw, ChevronRight } from 'lucide-react';

export default function EmailTemplateManagement() {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ subject: '', bodyContent: '', plainText: '' });
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    const fetchTemplates = useCallback(async () => {
        try {
            const res = await api.get('/admin/email-templates');
            setTemplates(res.data.templates || []);
        } catch {
            setToast({ type: 'error', message: 'Failed to load email templates' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    const openEditor = async (tpl) => {
        if (tpl.hasCustom) {
            try {
                const res = await api.get(`/admin/email-templates/${tpl.key}`);
                setForm({
                    subject: res.data.Subject || '',
                    bodyContent: res.data.BodyContent || '',
                    plainText: res.data.PlainText || '',
                });
            } catch {
                setForm({ subject: '', bodyContent: '', plainText: '' });
            }
        } else {
            setForm({ subject: '', bodyContent: '', plainText: '' });
        }
        setEditing(tpl);
    };

    const handleSave = async () => {
        if (!form.subject.trim() || !form.bodyContent.trim()) {
            setToast({ type: 'error', message: 'Subject and body content are required' });
            return;
        }
        setSaving(true);
        try {
            await api.put(`/admin/email-templates/${editing.key}`, {
                subject: form.subject.trim(),
                bodyContent: form.bodyContent.trim(),
                plainText: form.plainText.trim() || null,
            });
            setToast({ type: 'success', message: 'Template saved' });
            setEditing(null);
            fetchTemplates();
        } catch {
            setToast({ type: 'error', message: 'Failed to save template' });
        } finally {
            setSaving(false);
        }
    };

    const handleRevert = async () => {
        if (!window.confirm('Revert this email to its default? The custom template will be removed.')) return;
        setSaving(true);
        try {
            await api.delete(`/admin/email-templates/${editing.key}`);
            setToast({ type: 'success', message: 'Reverted to default' });
            setEditing(null);
            fetchTemplates();
        } catch {
            setToast({ type: 'error', message: 'Failed to revert template' });
        } finally {
            setSaving(false);
        }
    };

    if (!user?.isAdmin) return null;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h1><Mail size={24} /> Email Templates</h1>
                <p className="text-muted">Customise the subject and body of system emails. Changes take effect immediately.</p>
            </div>

            {toast && (
                <div className={`toast-message ${toast.type}`}>
                    {toast.message}
                </div>
            )}

            {loading ? (
                <div className="loading-spinner">Loading...</div>
            ) : editing ? (
                <div className="admin-card">
                    <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h2 style={{ margin: 0 }}>{editing.description}</h2>
                            <p className="text-muted" style={{ margin: '4px 0 0', fontSize: '13px' }}>
                                Key: <code>{editing.key}</code>
                            </p>
                        </div>
                        <button className="btn btn-ghost" onClick={() => setEditing(null)} title="Back to list">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div className="form-group">
                            <label className="form-label">Available variables</label>
                            <p className="text-muted" style={{ fontSize: '13px', margin: 0 }}>
                                Copy these into your subject or body: <code>{editing.variables}</code>
                            </p>
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="tpl-subject">Subject</label>
                            <input
                                id="tpl-subject"
                                className="form-input"
                                value={form.subject}
                                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                                placeholder="Email subject line"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="tpl-body">Body content (HTML)</label>
                            <textarea
                                id="tpl-body"
                                className="form-input"
                                rows={8}
                                value={form.bodyContent}
                                onChange={e => setForm(f => ({ ...f, bodyContent: e.target.value }))}
                                placeholder="The main message shown in the email"
                                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label" htmlFor="tpl-plain">Plain text version (optional)</label>
                            <textarea
                                id="tpl-plain"
                                className="form-input"
                                rows={4}
                                value={form.plainText}
                                onChange={e => setForm(f => ({ ...f, plainText: e.target.value }))}
                                placeholder="Falls back to body content if left empty"
                                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            {editing.hasCustom && (
                                <button className="btn btn-danger-outline" onClick={handleRevert} disabled={saving}>
                                    <RotateCcw size={16} /> Revert to default
                                </button>
                            )}
                            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                                <Save size={16} /> {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="admin-card">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Email type</th>
                                <th>Custom subject</th>
                                <th>Status</th>
                                <th style={{ width: 60 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map(tpl => (
                                <tr key={tpl.key}>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{tpl.description}</div>
                                        <div className="text-muted" style={{ fontSize: '12px' }}>{tpl.key}</div>
                                    </td>
                                    <td>{tpl.subject || <span className="text-muted">—</span>}</td>
                                    <td>
                                        {tpl.hasCustom ? (
                                            <span className="badge badge-success">Custom</span>
                                        ) : (
                                            <span className="badge badge-default">Default</span>
                                        )}
                                    </td>
                                    <td>
                                        <button className="btn btn-ghost btn-sm" onClick={() => openEditor(tpl)} title="Edit">
                                            <ChevronRight size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
